<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import CounterpartyPickerModal from '../components/CounterpartyPickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, toIsoDate } from '../utils/format'
import type {
  CurrencyOption,
  FinancialAccount,
  Invoice,
  Option,
  PaymentAllocationPayload,
  PaymentDirection,
  PaymentMethod,
  SupplierInvoice,
} from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const {
  listCurrencies,
  listAllAgentsByRole,
  listFinancialAccounts,
  listInvoices,
  listSupplierInvoices,
  getPayment,
  createPayment,
  updatePayment,
} = useFinanceApi()

const direction = computed<PaymentDirection>(() =>
  route.meta.direction === 'outflow' ? 'outflow' : 'inflow',
)
const basePath = computed(() =>
  direction.value === 'inflow' ? '/receivables/collections' : '/payables/payments',
)
const paymentId = computed(() => (route.params.id ? String(route.params.id) : ''))
const isEdit = computed(() => paymentId.value !== '')

const methods: PaymentMethod[] = ['cash', 'card', 'transfer']

const currencies = ref<CurrencyOption[]>([])
const accounts = ref<FinancialAccount[]>([])
interface OpenInvoiceOption {
  id: string | number
  label: string
  amount: number
}
const openInvoices = ref<OpenInvoiceOption[]>([])
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const formError = ref('')
const counterpartyPickerOpen = ref(false)
/** Display name of the picked counterparty; the id lives on the form. */
const counterpartyName = ref('')

interface AllocationDraft {
  invoice_id: string | number
  allocated_amount: number
  allocation_date: string
}

const form = ref({
  agent_id: '' as string | number,
  currency_code: '',
  exchange_rate: 1,
  financial_account_id: '' as string | number,
  amount: 0,
  payment_method: 'cash' as PaymentMethod,
  reference: '',
})

const allocations = ref<AllocationDraft[]>([])

const activeAccounts = computed(() => accounts.value.filter((account) => account.is_active === 1))

const allocatedTotal = computed(() =>
  allocations.value.reduce((sum, allocation) => sum + (Number(allocation.allocated_amount) || 0), 0),
)

const unallocatedAmount = computed(() => Number(form.value.amount || 0) - allocatedTotal.value)

const validationError = computed(() => {
  if (!form.value.agent_id) {
    return t('finance.common.requiredField', { field: t('finance.common.agent') })
  }
  if (!form.value.currency_code) {
    return t('finance.common.requiredField', { field: t('finance.common.currency') })
  }
  if (!form.value.financial_account_id) {
    return t('finance.common.requiredField', { field: t('finance.common.account') })
  }
  if (Number(form.value.amount) <= 0) {
    return t('finance.common.requiredField', { field: t('finance.common.amount') })
  }
  if (allocations.value.some((allocation) => !allocation.invoice_id || Number(allocation.allocated_amount) <= 0)) {
    return t('finance.payments.allocationIncomplete')
  }
  if (allocatedTotal.value > Number(form.value.amount)) {
    return t('finance.payments.allocationExceeds')
  }
  return ''
})

function addAllocation() {
  allocations.value.push({
    invoice_id: '',
    allocated_amount: Math.max(0, unallocatedAmount.value),
    allocation_date: toIsoDate(),
  })
}

function removeAllocation(index: number) {
  allocations.value.splice(index, 1)
}

/** Open documents of the selected counterparty become allocation targets. */
async function loadOpenInvoices(resetAllocations = true) {
  if (resetAllocations) allocations.value = []
  openInvoices.value = []
  if (!form.value.agent_id) return

  try {
    if (direction.value === 'inflow') {
      const invoices: Invoice[] = await listInvoices({ agent_id: form.value.agent_id })
      openInvoices.value = invoices
        .filter((invoice) => invoice.status === 'unpaid' || invoice.status === 'partial')
        .map((invoice) => ({
          id: invoice.id,
          label: `#${invoice.id} — ${formatMoney(invoice.total_amount, invoice.currency_code)}`,
          amount: Number(invoice.total_amount ?? 0),
        }))
    } else {
      const invoices: SupplierInvoice[] = await listSupplierInvoices({
        supplier_agent_id: form.value.agent_id,
      })
      openInvoices.value = invoices
        .filter((invoice) => invoice.status === 'open' || invoice.status === 'partial')
        .map((invoice) => ({
          id: invoice.id,
          label: `${invoice.invoice_number} — ${formatMoney(invoice.invoice_amount, invoice.currency_code)}`,
          amount: Number(invoice.invoice_amount ?? 0),
        }))
    }
  } catch (cause) {
    formError.value = formatApiError(cause)
  }
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const [currencyList, accountList] = await Promise.all([
      listCurrencies(),
      listFinancialAccounts({ is_active: 1 }),
    ])
    currencies.value = currencyList
    accounts.value = accountList

    if (!isEdit.value) {
      form.value.currency_code =
        currencyList.find((currency) => currency.is_base === 1)?.code || currencyList[0]?.code || ''
      form.value.financial_account_id = accountList[0]?.id ?? ''
      loading.value = false
      await applyQueryPrefill()
      return
    }

    const payment = await getPayment(paymentId.value)
    counterpartyName.value = payment.agent_name ?? ''
    form.value = {
      agent_id: payment.agent_id,
      currency_code: payment.currency_code,
      exchange_rate: Number(payment.exchange_rate ?? 1),
      financial_account_id: payment.financial_account_id,
      amount: Number(payment.amount ?? 0),
      payment_method: payment.payment_method,
      reference: payment.reference ?? '',
    }
    // Load the allocation targets first, then restore the saved allocations so
    // the existing rows are not wiped by the loading step.
    await loadOpenInvoices(false)
    const existing =
      direction.value === 'inflow'
        ? payment.customer_allocations ?? []
        : payment.supplier_allocations ?? payment.allocations ?? []
    allocations.value = existing.map((allocation) => ({
      invoice_id: allocation.invoice_id,
      allocated_amount: Number(allocation.allocated_amount ?? 0),
      allocation_date: allocation.allocation_date,
    }))
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

/** Counterparty chosen in the picker modal: reload its open documents. */
async function onCounterpartySelected(counterparty: Option) {
  counterpartyPickerOpen.value = false
  counterpartyName.value = counterparty.name
  form.value.agent_id = counterparty.id
  await loadOpenInvoices()
}

/**
 * "Record payment" on an invoice links here with `agent_id`, `invoice_id` and
 * `amount`, so the counterparty, the allocation target and the amount are all
 * ready when the form opens.
 */
async function applyQueryPrefill() {
  const agentParam = route.query.agent_id
  if (!agentParam) return

  form.value.agent_id = String(agentParam)
  const role = direction.value === 'inflow' ? 'customer' : 'supplier'
  try {
    const list = await listAllAgentsByRole(role)
    counterpartyName.value =
      list.find((agent) => String(agent.id) === String(agentParam))?.name ?? ''
  } catch {
    counterpartyName.value = ''
  }

  await loadOpenInvoices()

  const invoiceParam = route.query.invoice_id
  const matched = invoiceParam
    ? openInvoices.value.find((invoice) => String(invoice.id) === String(invoiceParam))
    : undefined
  if (!matched) return

  const amountParam = Number(route.query.amount)
  const amount = Number.isFinite(amountParam) && amountParam > 0 ? amountParam : matched.amount
  form.value.amount = amount
  allocations.value = [
    {
      invoice_id: matched.id,
      allocated_amount: amount,
      allocation_date: toIsoDate(),
    },
  ]
}

async function submit() {
  if (validationError.value) {
    formError.value = validationError.value
    return
  }
  saving.value = true
  formError.value = ''
  try {
    const payload: PaymentAllocationPayload[] = allocations.value.map((allocation) => ({
      invoice_id: allocation.invoice_id,
      allocated_amount: Number(allocation.allocated_amount),
      allocation_date: allocation.allocation_date,
    }))
    const body = {
      agent_id: form.value.agent_id,
      currency_code: form.value.currency_code,
      exchange_rate: Number(form.value.exchange_rate) || 1,
      direction: direction.value,
      financial_account_id: form.value.financial_account_id,
      amount: Number(form.value.amount),
      payment_method: form.value.payment_method,
      reference: form.value.reference.trim() || undefined,
      ...(direction.value === 'inflow'
        ? { customer_allocations: payload }
        : { supplier_allocations: payload }),
    }
    if (isEdit.value) {
      await updatePayment(paymentId.value, body)
    } else {
      await createPayment(body)
    }
    await router.push(basePath.value)
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

watch(direction, () => {
  formError.value = ''
})

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" :to="basePath">
      ← {{ direction === 'inflow' ? t('finance.nav.collections') : t('finance.nav.supplierPayments') }}
    </RouterLink>
    <h2>{{ isEdit ? t('finance.payments.editPayment') : t('finance.payments.newPayment') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="loadError" class="state-message error">
    <p>{{ loadError }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <form v-else class="stack" @submit.prevent="submit">
    <div class="form-card">
      <h3>{{ t('finance.payments.newPayment') }}</h3>
      <div class="form-grid">
        <div class="field">
          <label for="payment-agent">{{ t('finance.common.agent') }}</label>
          <button
            id="payment-agent"
            class="field-button"
            type="button"
            @click="counterpartyPickerOpen = true"
          >
            <span v-if="counterpartyName">{{ counterpartyName }}</span>
            <span v-else class="placeholder">{{ t('finance.picker.agentTitle') }}</span>
            <span aria-hidden="true">⌕</span>
          </button>
        </div>
        <div class="field">
          <label for="payment-account">{{ t('finance.common.account') }}</label>
          <select id="payment-account" v-model="form.financial_account_id">
            <option value="">{{ t('finance.common.allAccounts') }}</option>
            <option v-for="account in activeAccounts" :key="account.id" :value="account.id">
              {{ account.name }} — {{ account.currency_code }}
            </option>
          </select>
        </div>
        <div class="field">
          <label for="payment-amount">{{ t('finance.common.amount') }}</label>
          <input id="payment-amount" v-model.number="form.amount" type="number" step="0.01">
        </div>
        <div class="field">
          <label for="payment-currency">{{ t('finance.common.currency') }}</label>
          <select id="payment-currency" v-model="form.currency_code">
            <option value="">{{ t('finance.common.allCurrencies') }}</option>
            <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
              {{ currency.code }} — {{ currency.name }}
            </option>
          </select>
        </div>
        <div class="field">
          <label for="payment-rate">{{ t('finance.common.exchangeRate') }}</label>
          <input id="payment-rate" v-model.number="form.exchange_rate" type="number" step="0.000001">
        </div>
        <div class="field">
          <label for="payment-method">{{ t('finance.common.method') }}</label>
          <select id="payment-method" v-model="form.payment_method">
            <option v-for="method in methods" :key="method" :value="method">
              {{ t(`finance.paymentMethod.${method}`) }}
            </option>
          </select>
        </div>
        <div class="field field-wide">
          <label for="payment-reference">{{ t('finance.common.reference') }}</label>
          <input id="payment-reference" v-model="form.reference" type="text" autocomplete="off">
        </div>
      </div>
    </div>

    <div class="form-card">
      <div class="panel-header">
        <h3>
          {{ t('finance.receivables.allocations') }}
          <small>
            {{ t('finance.payments.allocatedTotal') }}:
            {{ formatMoney(allocatedTotal, form.currency_code) }} ·
            {{ t('finance.payments.unallocated') }}:
            {{ formatMoney(unallocatedAmount, form.currency_code) }}
          </small>
        </h3>
        <button class="btn-secondary" type="button" @click="addAllocation">
          {{ t('finance.payments.addAllocation') }}
        </button>
      </div>

      <p v-if="!form.agent_id" class="empty-inline">{{ t('finance.payments.selectCounterparty') }}</p>
      <p v-else-if="openInvoices.length === 0" class="empty-inline">
        {{ t('finance.payments.noOpenInvoices') }}
      </p>
      <p v-else-if="direction === 'inflow' && unallocatedAmount > 0" class="empty-inline">
        {{ t('finance.payments.advanceNotice') }}
      </p>

      <div v-for="(allocation, index) in allocations" :key="index" class="allocation-row">
        <div class="field">
          <label :for="`allocation-invoice-${index}`">{{ t('finance.common.invoice') }}</label>
          <select :id="`allocation-invoice-${index}`" v-model="allocation.invoice_id">
            <option value="">{{ t('finance.payments.selectAllocationInvoice') }}</option>
            <option v-for="invoice in openInvoices" :key="invoice.id" :value="invoice.id">
              {{ invoice.label }}
            </option>
          </select>
        </div>
        <div class="field">
          <label :for="`allocation-amount-${index}`">{{ t('finance.receivables.allocatedAmount') }}</label>
          <input
            :id="`allocation-amount-${index}`"
            v-model.number="allocation.allocated_amount"
            type="number"
            step="0.01"
          >
        </div>
        <div class="field">
          <label :for="`allocation-date-${index}`">{{ t('finance.receivables.allocationDate') }}</label>
          <input :id="`allocation-date-${index}`" v-model="allocation.allocation_date" type="date">
        </div>
        <button
          class="btn-secondary allocation-remove"
          type="button"
          @click="removeAllocation(index)"
        >
          {{ t('finance.common.clear') }}
        </button>
      </div>
    </div>

    <p v-if="formError" class="error-inline">{{ formError }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        {{ saving ? t('finance.common.saving') : t('finance.common.save') }}
      </button>
      <RouterLink class="btn-secondary" :to="basePath">{{ t('finance.common.cancel') }}</RouterLink>
    </div>

    <CounterpartyPickerModal
      :open="counterpartyPickerOpen"
      :role="direction === 'inflow' ? 'customer' : 'supplier'"
      :selected-id="form.agent_id || null"
      @close="counterpartyPickerOpen = false"
      @select="onCounterpartySelected"
    />
  </form>
</template>
